import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { JobListCard } from '../opportunities/OpportunityComponents';
import { useLanguage } from '../contexts/LanguageContext';
import { browseScoutJobs } from '../scoutApi';
import ScoutInventoryFilters, { inventoryValueLabel } from './ScoutInventoryFilters';
import './ScoutInventoryBrowser.css';

const COPY = {
  en: {
    loading: 'Loading jobs…', empty: 'No jobs found. Try changing your filters.',
    error: 'We could not load these jobs. Please try again.', retry: 'Try again', previous: 'Previous', next: 'Next',
    page: 'Page {page} of {pages}', posted: 'Posted', added: 'Added',
    noScout: 'Search by job title or location to find jobs.', applied: 'Applied', pagination: 'Job pages',
  },
  ar: {
    loading: 'جارٍ تحميل الوظائف…', empty: 'لم نجد وظائف. جرّب تغيير خيارات البحث.',
    error: 'تعذّر تحميل الوظائف. يرجى المحاولة مجدداً.', retry: 'حاول مجدداً', previous: 'السابق', next: 'التالي',
    page: 'الصفحة {page} من {pages}', posted: 'نُشرت في', added: 'أُضيفت في',
    noScout: 'ابحث بالمسمّى الوظيفي أو الموقع للعثور على وظائف.', applied: 'تم التقديم', pagination: 'صفحات الوظائف',
  },
};
const EMPTY_FILTERS = { q: '', location: '' };

export default function ScoutInventoryBrowser({ user, appliedJobIds = new Set(), onJobClick, selectedJobId, initialState, compact = false }) {
  const { language } = useLanguage();
  const copy = COPY[language] || COPY.en;
  const navigate = useNavigate();
  const [draft, setDraft] = useState(initialState?.draft || EMPTY_FILTERS);
  const [filters, setFilters] = useState(initialState?.filters || EMPTY_FILTERS);
  const [page, setPage] = useState(initialState?.page || 1);
  const [result, setResult] = useState(null);
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const listTop = useRef(null);
  const locale = language === 'ar' ? 'ar-LB' : 'en-US';
  const fill = (text, values) => Object.entries(values).reduce((s, [key, value]) => s.replace(`{${key}}`, new Intl.NumberFormat(locale).format(value)), text);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    setResult(null);
    if (!user) { setLoading(false); return () => controller.abort(); }
    browseScoutJobs({ user, ...filters, page, page_size: 20, signal: controller.signal })
      .then((data) => { if (!controller.signal.aborted) { setResult(data); setFacets(data.facets || {});
        if (data.searchDefaults && !filters.q && !filters.location && filters.use_saved_preferences !== false) {
          setDraft((current) => ({ ...data.searchDefaults, ...Object.fromEntries(Object.entries(current).filter(([, value]) => value !== '')) }));
        } } })
      .catch(() => { if (!controller.signal.aborted) setError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [user, filters, page, retry]);

  const pages = Math.max(1, Math.ceil((result?.total || 0) / 20));
  const changePage = (next) => {
    setPage(next);
    listTop.current?.scrollTo({ top: 0 });
  };
  return <div className={`js-inventory-browser opp-inventory ${compact ? 'is-compact' : ''}`} ref={listTop}>
    <ScoutInventoryFilters values={draft} onChange={setDraft} facets={facets} showLocation={result?.state === 'preferences_required' || Boolean(result?.searchDefaults && Object.keys(result.searchDefaults).length)}
      onSubmit={(next = draft) => { setDraft(next); setPage(1); setFilters({ ...next, use_saved_preferences: false }); listTop.current?.scrollTo({ top: 0 }); }} />
    <div aria-live="polite">
      {loading && <p className="js-apply-muted" role="status">{copy.loading}</p>}
      {error && <div role="alert"><p>{copy.error}</p><div className="js-scout-tabs"><button type="button" onClick={() => setRetry(retry + 1)}>{copy.retry}</button></div></div>}
      {result && !result.jobs.length && <p>{result.state === 'preferences_required' ? copy.noScout : copy.empty}</p>}
    </div>
    <div className="opp-job-list">
      {(result?.jobs || []).map((job) => <JobListCard job={{ ...job, id: job.jobId || job.id }} badge={appliedJobIds.has(job.jobId || job.id) ? copy.applied : undefined} active={selectedJobId === (job.jobId || job.id)} key={job.jobId || job.id}
        onClick={() => {
          const nextJob = { ...job, id: job.jobId || job.id, jobOpening: job.title || job.jobOpening, companyLocation: job.location || job.companyLocation,
            workspace: job.remoteType ? inventoryValueLabel(job.remoteType, language) : job.workspace,
            schedule: job.employmentType ? inventoryValueLabel(job.employmentType, language) : job.schedule };
          if (onJobClick) onJobClick(nextJob, { draft, filters, page });
          else navigate(`/job/${encodeURIComponent(nextJob.id)}?inventory=1`, { state: { job: nextJob, browseState: { draft, filters, page } } });
        }} />)}
    </div>
    {result?.total > 0 && <nav className="js-scout-tabs js-inventory-pagination" aria-label={copy.pagination}>
      <button type="button" disabled={page === 1 || loading} onClick={() => changePage(page - 1)}>{copy.previous}</button>
      <span>{fill(copy.page, { page, pages })}</span>
      <button type="button" disabled={page >= pages || loading} onClick={() => changePage(page + 1)}>{copy.next}</button>
    </nav>}
  </div>;
}
